import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { getDonationStatus } from "@/lib/api";

const MAX_POLLS = 8;
const POLL_INTERVAL = 2500;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", error: "Missing session id.", data: null });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      attempts.current += 1;
      try {
        const data = await getDonationStatus(sessionId);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data, error: null });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data, error: null });
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setState({ phase: "pending", data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (cancelled) return;
        setState({ phase: "error", data: null, error: err?.response?.data?.detail || "Could not fetch status." });
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white">
      <Nav />
      <main className="pt-32 pb-24">
        <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {state.phase === "checking" && (
            <div data-testid="status-checking">
              <Loader2 className="w-12 h-12 text-blue-700 mx-auto animate-spin" strokeWidth={1.5} />
              <h1 className="mt-8 font-display text-3xl md:text-5xl font-black tracking-tighter">
                Confirming your gift…
              </h1>
              <p className="mt-4 text-slate-600">One moment while we verify your donation with Stripe.</p>
            </div>
          )}

          {state.phase === "paid" && (
            <div data-testid="status-paid">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" strokeWidth={1.5} />
              <div className="eyebrow text-blue-700 mt-6">Thank you</div>
              <h1 className="mt-2 font-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-slate-900">
                Your gift is received.
              </h1>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Because of you, a grieving family will be fed. A weary officer will be seen and
                supported.
                A department in crisis will feel less alone. Thank you for standing with those who
                serve.
              </p>
              {state.data?.amount && (
                <div className="mt-8 inline-block border border-slate-200 px-8 py-6">
                  <div className="eyebrow text-slate-500">Amount</div>
                  <div className="font-display text-4xl font-black">
                    ${Number(state.data.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>
              )}
              <div className="mt-10">
                <Link
                  to="/"
                  data-testid="status-home-cta"
                  className="inline-flex items-center gap-3 bg-black hover:bg-neutral-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors"
                >
                  Return home <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {state.phase === "pending" && (
            <div data-testid="status-pending">
              <Loader2 className="w-12 h-12 text-blue-700 mx-auto animate-spin" strokeWidth={1.5} />
              <h1 className="mt-8 font-display text-3xl md:text-5xl font-black tracking-tighter">
                Still processing…
              </h1>
              <p className="mt-4 text-slate-600">
                Your payment is still being confirmed. You'll receive a receipt by email once it's complete.
              </p>
              <Link
                to="/"
                data-testid="status-home-cta"
                className="mt-8 inline-flex items-center gap-3 border-2 border-black hover:bg-black hover:text-white text-slate-900 px-8 py-4 text-xs font-bold uppercase tracking-widest transition-all"
              >
                Return home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {state.phase === "expired" && (
            <div data-testid="status-expired">
              <AlertCircle className="w-12 h-12 text-blue-700 mx-auto" strokeWidth={1.5} />
              <h1 className="mt-8 font-display text-3xl md:text-5xl font-black tracking-tighter">
                Session expired
              </h1>
              <p className="mt-4 text-slate-600">The checkout session expired without completing. You can try again anytime.</p>
              <Link
                to="/donate"
                data-testid="status-retry-cta"
                className="mt-8 inline-flex items-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Try again <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {state.phase === "error" && (
            <div data-testid="status-error">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto" strokeWidth={1.5} />
              <h1 className="mt-8 font-display text-3xl md:text-5xl font-black tracking-tighter">
                Something went wrong
              </h1>
              <p className="mt-4 text-slate-600">{state.error}</p>
              <Link
                to="/donate"
                data-testid="status-retry-cta"
                className="mt-8 inline-flex items-center gap-3 bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 text-xs font-bold uppercase tracking-widest transition-colors"
              >
                Try again <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
